import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import Spinner from 'components/ui-elements/Spinner';
import { useLogout } from 'features/account';
import { useBecomeFundraiserPopup } from 'features/my-profile/become-fundraiser/form/useBecomeFundraiserPopup';
import { useCurrentUser, usePaymentConfig } from 'providers/cache/cachedDataHooks';
import { getAuthType } from 'utils/localStorageData';

import { MenuItem } from './MenuItem';
import { MenuData } from './menuTypes';

const Container = styled.div`
  display: flex;
  position: relative;
  min-width: 216px;
`;

const MenuItemsWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 216px;
  margin: 36px 0 36px 24px;

  ${theme.mq.desktop} {
    flex-direction: row;
    margin: 0;
    width: 100%;
    overflow-x: scroll;
    overflow-y: hidden;
  }
`;

const Gradient = styled.div`
  width: 20px;
  height: 100%;
  position: absolute;
  background: linear-gradient(90deg, #ffffff00 0%, #ffffff 100%);
  display: none;

  ${theme.mq.desktop} {
    display: flex;
  }
`;

const GradientLeft = styled(Gradient)`
  left: -1px;
  transform: rotate(180deg);
`;

const GradientRight = styled(Gradient)`
  right: -1px;
`;

type DisabledOptions = { [key in string]: boolean };
export const Menu = () => {
  const navigate = useNavigate();
  const { logout } = useLogout();
  const { currentUser } = useCurrentUser();
  const { paymentConfig } = usePaymentConfig();
  const { showBecomeFundraiserPopup } = useBecomeFundraiserPopup();
  const authType = getAuthType();

  const canStartBecomeFundraiserFlow =
    !!currentUser &&
    !!currentUser.ableToBecomeFundraiser &&
    !currentUser.ableToCreateCampaigns &&
    !currentUser.fundraiserVerificationCompleted;
  const canEditBecomeFundraiserFlow =
    !!currentUser &&
    !!currentUser.ableToBecomeFundraiser &&
    currentUser.ableToCreateCampaigns &&
    !currentUser.fundraiserVerificationCompleted;

  const disabledOptions: DisabledOptions = {
    'payment.header': !(paymentConfig?.availablePaymentMethodTypes?.includes('creditCard') ?? true),
    'become-fundraiser.header': !canStartBecomeFundraiserFlow,
    'become-fundraiser.edit-form': !canEditBecomeFundraiserFlow,
  };

  const menuConfig: MenuData[] = [
    {
      nameKey: 'my-activity.header',
      url: '/my-profile/my-activity',
      subItems: [
        { nameKey: 'myProfile:my-activity.campaigns', url: '/my-profile/my-activity/campaigns' },
        {
          nameKey: 'myProfile:my-activity.hot-deals',
          url: '/my-profile/my-activity/selling',
          activeForUrls: [
            '/my-profile/my-activity/selling',
            '/my-profile/my-activity/donated',
            '/my-profile/my-activity/bought',
          ],
        },
      ],
    },
    { nameKey: 'account-settings.header', url: '/my-profile/account-settings' },
    { nameKey: 'location.header', url: '/my-profile/location' },
    { nameKey: 'shipping-address.header', url: '/my-profile/shipping-address' },
    { nameKey: 'payment.header', url: '/my-profile/payment' },
    { nameKey: 'contact-support.header', onClick: () => navigate('/messenger/support') },
    { nameKey: 'notifications.header', url: '/my-profile/notifications' },
    { nameKey: 'password.header', url: '/my-profile/password', isHidden: authType && authType !== 'Email' },
    { nameKey: '2fa.header', isDisabled: true },
    { nameKey: 'become-fundraiser.header', onClick: () => showBecomeFundraiserPopup(currentUser!) },
    { nameKey: 'become-fundraiser.edit-form', onClick: () => showBecomeFundraiserPopup(currentUser!) },
    { nameKey: 'logout', onClick: logout },
  ];

  const filteredMenuConfig = useMemo(
    () =>
      menuConfig.filter(menuOption => {
        return !disabledOptions[menuOption.nameKey];
      }),
    [menuConfig]
  );

  if (!currentUser)
    return (
      <Container>
        <Spinner />
      </Container>
    );

  return (
    <Container data-testid={'my-profile-menu'}>
      <MenuItemsWrapper>
        {filteredMenuConfig.map(menuData => {
          return !menuData.isHidden && <MenuItem key={menuData.nameKey} {...menuData} />;
        })}
      </MenuItemsWrapper>
      <GradientLeft />
      <GradientRight />
    </Container>
  );
};
